import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { buildPalette, applyPalette, loadCommunityFonts, type ThemePalette } from '../lib/theme'

interface ThemeState {
  palette: ThemePalette
  isCommunity: boolean
}

const ThemeCtx = createContext<ThemeState | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  const isCommunity = user?.userType === 'community'

  const palette = useMemo(
    () => buildPalette(
      !!isCommunity,
      user?.communityAccent,
      user?.communityFont,
    ),
    [isCommunity, user?.communityAccent, user?.communityFont],
  )

  // Apply CSS variables whenever palette changes
  useEffect(() => {
    applyPalette(palette)
    if (isCommunity) {
      loadCommunityFonts(user?.communityFont)
    }
  }, [palette, isCommunity, user?.communityFont])

  return (
    <ThemeCtx.Provider value={{ palette, isCommunity: !!isCommunity }}>
      {children}
    </ThemeCtx.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeCtx)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
